import { useParams, Navigate, Link } from "react-router-dom";
import serviceLocations from "@/data/service-locations.data.json";
import cities from "@/data/cities.data.json";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Check, ArrowRight, MapPin } from "lucide-react";

const SITE = "https://www.spectrumservice.it";

export default function ServiceLocation() {
  const { slug } = useParams();
  const svc = serviceLocations.find((s) => s.slug === slug);
  if (!svc) return <Navigate to="/" replace />;

  const city = cities.find((c) => c.slug === svc.city_slug);
  const canonical = `${SITE}/servizi/${svc.slug}`;

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: svc.h1,
    description: svc.description,
    provider: {
      "@type": "LocalBusiness",
      name: "Spectrum Service",
      url: SITE,
    },
    areaServed: {
      "@type": "City",
      name: city ? city.name : "",
    },
    offers: {
      "@type": "Offer",
      price: svc.price_from,
      priceCurrency: "EUR",
      priceSpecification: {
        "@type": "PriceSpecification",
        price: svc.price_from,
        priceCurrency: "EUR",
        valueAddedTaxIncluded: false,
      },
    },
  };

  return (
    <div data-testid={`service-location-${svc.slug}`} className="min-h-screen">
      <SEO
        title={svc.title}
        description={svc.description}
        canonical={canonical}
        type="website"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceLd) }}
      />

      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Servizi", to: "/catalogo" },
          ...(city ? [{ label: city.name, to: `/${city.slug}` }] : []),
          { label: svc.h1 },
        ]}
      />

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-10 sm:py-14">
        {city && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-rose-500 mb-3 flex items-center gap-1.5">
            <MapPin size={12} /> {city.name}
          </div>
        )}
        <h1 className="font-heading text-3xl sm:text-5xl font-black uppercase tracking-tight text-white leading-[1] mb-5">
          {svc.h1}
        </h1>
        <p className="text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed mb-8">
          {svc.intro}
        </p>

        <div className="inline-flex items-baseline gap-2 mb-8">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            Da
          </div>
          <div className="font-mono font-black text-3xl text-rose-500">
            €{svc.price_from}
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            IVA esclusa
          </div>
        </div>

        <ul className="space-y-2.5 mb-10">
          {svc.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3">
              <Check
                size={16}
                className="text-emerald-400 shrink-0 mt-0.5"
              />
              <span className="text-sm sm:text-base text-zinc-200 leading-relaxed">
                {b}
              </span>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-3">
          <Link
            data-testid={`service-cta-${svc.slug}`}
            to="/preventivo"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-mono uppercase tracking-widest transition"
          >
            {svc.cta} <ArrowRight size={14} />
          </Link>
          <Link
            to="/catalogo"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white text-xs font-mono uppercase tracking-widest transition"
          >
            Vedi il catalogo completo
          </Link>
        </div>

        {city && (
          <div className="mt-14 pt-8 border-t border-zinc-900">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
              Scopri di più
            </div>
            <Link
              to={`/${city.slug}`}
              className="inline-flex items-center gap-2 text-sm text-rose-400 hover:text-rose-300"
            >
              Tutti i servizi Spectrum a {city.name} <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
